import {
  Theme,
  type WithStyles,
  createStyles,
  withStyles,
} from "@material-ui/core";
import { Button } from "@material-ui/core";
import { useTranslations } from "adapters/i18n";
import React from "react";
import type { UserManagementFilter } from "../../userManagement.types";
import UserList from "./components/userList";
import UserListFilter from "./components/userListFilter";
import type { GetUsersFn, User } from "./userList.types";
import { isManagementFilterEmpty } from "./userList.utils";

type UserListPageProps = {
  clientId: number | null;
  filter: UserManagementFilter;
  getUsers: GetUsersFn;
  lastUpdate: number;
  onChangeStatus: (user: User) => void;
  onCopyLink: (user: User) => Promise<void>;
  onEdit: (user: User) => void;
  onFilterChange: (filter: UserManagementFilter) => void;
  onNewUser: () => void;
  onSendLink: (user: User) => Promise<void>;
} & WithStyles<typeof styles>;

const UserListPage = ({
  classes,
  clientId,
  filter,
  getUsers,
  lastUpdate,
  onChangeStatus,
  onCopyLink,
  onEdit,
  onFilterChange,
  onNewUser,
  onSendLink,
}: UserListPageProps) => {
  const { t } = useTranslations();
  const isFilterEmpty = isManagementFilterEmpty(filter);

  return (
    <div>
      <section className={classes.title}>{t("user:registered-users")}</section>
      <section className={classes.filter}>
        <UserListFilter
          clientId={clientId}
          filter={filter}
          onFilterChange={onFilterChange}
        />
        <Button
          variant="contained"
          className={classes.button}
          onClick={onNewUser}
        >
          {t("user:create-new-user")}
        </Button>
      </section>
      <section className={classes.list}>
        <UserList
          isFilterEmpty={isFilterEmpty}
          getUsers={getUsers}
          lastUpdate={lastUpdate}
          onEdit={onEdit}
          onSendLink={onSendLink}
          onCopyLink={onCopyLink}
          onChangeStatus={onChangeStatus}
        />
      </section>
    </div>
  );
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      padding: "40px",
    },
    title: {
      fontFamily: "Roboto",
      fontSize: "20px",
      fontWeight: 700,
      lineHeight: "23.44px",
      textAlign: "left",
      color: "#444444",
    },
    button: {
      width: "188px",
      height: "36px",
      borderRadius: "18px",
      whiteSpace: "nowrap",
      color: theme.palette.common.white,
      backgroundColor: theme.palette.primary.main,
      "&:hover": {
        backgroundColor: theme.palette.primary.main,
        filter: "brightness(0.8)",
      },
    },
    filter: {
      display: "grid",
      gridTemplateColumns: "1fr auto",
      alignItems: "center",
      marginTop: "35px",
      "@media (max-width: 1024px)": {
        gridTemplateColumns: "auto",
        marginTop: "16px",
      },
    },
    list: {
      marginTop: "40px",
    },
  });

export default withStyles(styles)(UserListPage);
